// DashboardLayout.tsx - Shared layout component with role-based routing
import { useState, useRef, useEffect } from "react";
import {
  Link,
  Outlet,
  useLocation,
  NavLink,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { showSuccess } from "../../lib/toast";
import {
  coursesApi,
  mentorshipApi,
  wishlistApi,
  notificationApi,
} from "../../lib";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Calendar,
  MessageCircle,
  Settings,
  HelpCircle,
  LogOut,
  Bell,
  Search,
  Menu,
  GraduationCap,
  Award,
  TrendingUp,
  Video,
  FileText,
  Star,
  UserCheck,
  Shield,
  DollarSign,
  Flag,
  FolderOpen,
  MessagesSquare,
  User,
  Home,
  ChevronDown,
  Layers,
  Trophy,
  X,
  Ticket,
  Mail,
  Activity,
  Heart,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Medal,
  Zap,
  CreditCard,
} from "lucide-react";

interface NavItem {
  name: string;
  icon: any;
  href: string;
  roles: string[];
  badge?: number;
}

// Role-specific navigation items
const navItems: NavItem[] = [
  // Student specific
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    href: "/dashboard/student",
    roles: ["student"],
  },
  {
    name: "My Courses",
    icon: BookOpen,
    href: "/dashboard/student/courses",
    roles: ["student"],
  },
  {
    name: "Quizzes",
    icon: FileText,
    href: "/dashboard/student/quizzes",
    roles: ["student"],
    badge: 2,
  },
  {
    name: "Certificates",
    icon: Award,
    href: "/dashboard/student/certificates",
    roles: ["student"],
  },
  {
    name: "My Sessions",
    icon: Video,
    href: "/dashboard/student/mentorship-sessions",
    roles: ["student"],
  },
  {
    name: "Schedule",
    icon: Calendar,
    href: "/dashboard/student/schedule",
    roles: ["student"],
  },
  {
    name: "Messages",
    icon: MessageCircle,
    href: "/dashboard/student/messages",
    roles: ["student"],
  },
  {
    name: "Wishlist",
    icon: Star,
    href: "/dashboard/student/wishlist",
    roles: ["student"],
  },
  {
    name: "Analytics",
    icon: TrendingUp,
    href: "/dashboard/student/analytics",
    roles: ["student"],
  },
  {
    name: "Forums",
    icon: MessagesSquare,
    href: "/dashboard/student/forums",
    roles: ["student"],
  },
  {
    name: "Badges",
    icon: Award,
    href: "/dashboard/badges",
    roles: ["student"],
  },
  {
    name: "Leaderboard",
    icon: Trophy,
    href: "/leaderboard",
    roles: ["student"],
  },

  // Mentor specific
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    href: "/dashboard/mentor",
    roles: ["mentor"],
  },
  {
    name: "Mentor Profile",
    icon: User,
    href: "/dashboard/mentor/profile",
    roles: ["mentor"],
  },
  {
    name: "My Mentees",
    icon: Users,
    href: "/dashboard/mentor/mentees",
    roles: ["mentor"],
    badge: 5,
  },
  {
    name: "Sessions",
    icon: Video,
    href: "/dashboard/mentor/sessions",
    roles: ["mentor"],
  },
  {
    name: "Schedule",
    icon: Calendar,
    href: "/dashboard/mentor/schedule",
    roles: ["mentor"],
  },
  {
    name: "Messages",
    icon: MessageCircle,
    href: "/dashboard/mentor/messages",
    roles: ["mentor"],
  },
  {
    name: "Earnings",
    icon: DollarSign,
    href: "/dashboard/mentor/earnings",
    roles: ["mentor"],
  },
  {
    name: "Analytics",
    icon: TrendingUp,
    href: "/dashboard/mentor/analytics",
    roles: ["mentor"],
  },
  {
    name: "Resources",
    icon: FolderOpen,
    href: "/dashboard/mentor/resources",
    roles: ["mentor"],
  },

  // Instructor specific
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    href: "/dashboard/instructor",
    roles: ["instructor"],
  },
  {
    name: "My Courses",
    icon: BookOpen,
    href: "/dashboard/instructor/courses",
    roles: ["instructor"],
  },
  {
    name: "Create Course",
    icon: FileText,
    href: "/dashboard/instructor/create-course",
    roles: ["instructor"],
  },
  {
    name: "Certificate Exams",
    icon: Award,
    href: "/dashboard/instructor/certificate-exam",
    roles: ["instructor"],
  },
  {
    name: "Students",
    icon: Users,
    href: "/dashboard/instructor/students",
    roles: ["instructor"],
    badge: 12,
  },
  {
    name: "Analytics",
    icon: TrendingUp,
    href: "/dashboard/instructor/analytics",
    roles: ["instructor"],
  },
  {
    name: "Revenue",
    icon: DollarSign,
    href: "/dashboard/instructor/revenue",
    roles: ["instructor"],
  },
  {
    name: "Reviews",
    icon: Star,
    href: "/dashboard/instructor/reviews",
    roles: ["instructor"],
    badge: 4,
  },
  {
    name: "Messages",
    icon: MessageCircle,
    href: "/dashboard/instructor/messages",
    roles: ["instructor"],
    badge: 2,
  },

  // Admin specific
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    href: "/dashboard/admin",
    roles: ["admin"],
  },
  {
    name: "Users",
    icon: Users,
    href: "/dashboard/admin/users",
    roles: ["admin"],
  },
  {
    name: "Courses",
    icon: BookOpen,
    href: "/dashboard/admin/courses",
    roles: ["admin"],
  },
  {
    name: "Certificate Exams",
    icon: Award,
    href: "/dashboard/admin/certificate-exam",
    roles: ["admin"],
  },
  {
    name: "Instructors",
    icon: UserCheck,
    href: "/dashboard/admin/instructors",
    roles: ["admin"],
  },
  {
    name: "Mentors",
    icon: Shield,
    href: "/dashboard/admin/mentors",
    roles: ["admin"],
  },
  {
    name: "Applications",
    icon: FileText,
    href: "/dashboard/admin/applications",
    roles: ["admin"],
  },
  {
    name: "Students",
    icon: GraduationCap,
    href: "/dashboard/admin/students",
    roles: ["admin"],
  },
  {
    name: "Mentorship Sessions",
    icon: Calendar,
    href: "/dashboard/admin/sessions",
    roles: ["admin"],
  },
  {
    name: "Revenue",
    icon: DollarSign,
    href: "/dashboard/admin/revenue",
    roles: ["admin"],
  },
  {
    name: "Reports",
    icon: Flag,
    href: "/dashboard/admin/reports",
    roles: ["admin"],
    badge: 3,
  },
  {
    name: "Security Settings",
    icon: Settings,
    href: "/dashboard/admin/settings",
    roles: ["admin"],
  },
  {
    name: "Transactions",
    icon: CreditCard,
    href: "/dashboard/admin/transactions",
    roles: ["admin"],
  },
  {
    name: "Bulk Operations",
    icon: Layers,
    href: "/dashboard/admin/bulk-operations",
    roles: ["admin"],
  },
  {
    name: "Contact Messages",
    icon: Mail,
    href: "/dashboard/admin/contacts",
    roles: ["admin"],
  },
  {
    name: "Activity Logs",
    icon: Activity,
    href: "/dashboard/admin/activity-logs",
    roles: ["admin"],
  },
  {
    name: "Coupons & Whitelist",
    icon: Ticket,
    href: "/dashboard/admin/coupons",
    roles: ["admin"],
  },
];

// Common items for all roles
const commonItems: NavItem[] = [
  {
    name: "Notifications",
    icon: Bell,
    href: "/notifications",
    roles: ["student", "mentor", "instructor", "admin"],
  },
  {
    name: "Help & Support",
    icon: HelpCircle,
    href: "/docs",
    roles: ["student", "mentor", "instructor", "admin"],
  },
];

interface DashboardLayoutProps {
  userRole: "student" | "mentor" | "instructor" | "admin";
}

const DashboardLayout = ({ userRole }: DashboardLayoutProps) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mentorUnreadCount, setMentorUnreadCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{
    courses: any[];
    forums: any[];
    discussions: any[];
    mentors: any[];
  }>({ courses: [], forums: [], discussions: [], mentors: [] });
  const [searching, setSearching] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Helper function to format badge count (show 9+ for numbers > 9)
  const formatBadgeCount = (count: number): string => {
    if (count > 99) return "99+";
    if (count > 9) return "9+";
    return count.toString();
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(false);
      }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch mentorship messages unread count
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        const mentorRes = await mentorshipApi.getUnreadCount();
        if (mentorRes.success) {
          setMentorUnreadCount(mentorRes.data?.totalUnread || 0);
        }
      } catch (err) {
        console.error("Failed to fetch mentor unread count:", err);
      }
      try {
        const notifRes = await notificationApi.getUnreadCount();
        if (notifRes.success) {
          setUnreadNotifications(notifRes.unreadCount || 0);
        }
      } catch (err) {
        console.error("Failed to fetch notification unread count:", err);
      }
    };

    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch wishlist count for students
  useEffect(() => {
    const fetchWishlistCount = async () => {
      if (userRole === "student" && user?._id) {
        try {
          setWishlistLoading(true);
          const res = await wishlistApi.getMyWishlist({ limit: 1 });
          if (res.success) {
            setWishlistCount(res.pagination.total);
          }
        } catch (err) {
          console.error("Failed to fetch wishlist count:", err);
        } finally {
          setWishlistLoading(false);
        }
      }
    };

    fetchWishlistCount();
  }, [userRole, user?._id]);

  // Combine role-specific items with common items
  const roleSpecificItems = navItems.filter((item) =>
    item.roles.includes(userRole),
  );
  const filteredNavItems = [...roleSpecificItems, ...commonItems].map(
    (item) => {
      if (item.name === "Messages") {
        const messageBadge =
          (userRole === "student" || userRole === "mentor") &&
          mentorUnreadCount > 0
            ? mentorUnreadCount
            : undefined;
        return { ...item, badge: messageBadge };
      }
      if (item.name === "Notifications") {
        return {
          ...item,
          badge: unreadNotifications > 0 ? unreadNotifications : undefined,
        };
      }
      return item;
    },
  );

  const getUserName = () => {
    if (user) {
      return user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.username || user.email;
    }
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      return userData.firstName && userData.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : userData.username || userData.email;
    }
    switch (userRole) {
      case "student":
        return "Student User";
      case "mentor":
        return "Mentor User";
      case "instructor":
        return "Instructor User";
      case "admin":
        return "Admin User";
      default:
        return "User";
    }
  };

  const getUserAvatar = (): string | null => {
    if (user?.avatar) return user.avatar;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      return userData?.avatar || null;
    }
    return null;
  };

  const getUserInitials = () => {
    const name = getUserName();
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    setIsProfileOpen(false);
    await logout();
    showSuccess("Logged out successfully!");
    navigate("/");
  };

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setShowLogoutModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowLogoutModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close sidebar on mobile when clicking a link
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Build profile menu items dynamically based on user role
  const getProfileMenuItems = () => {
    const baseItems = [
      { name: "Profile", icon: User, href: "/profile" },
      { name: "Home", icon: Home, href: "/" },
    ];
    
    // Add Badges link only for students
    if (userRole === "student") {
      baseItems.push({ name: "Badges", icon: Medal, href: "/dashboard/badges" });
      baseItems.push({ name: "My transactions", icon: Zap, href: "/dashboard/student/transactions" });
    }
    
    baseItems.push({ name: "Settings", icon: Settings, href: "/settings" });
    baseItems.push({ name: "Logout", icon: LogOut, href: "#", onClick: handleLogoutClick });
    
    return baseItems;
  };

  const profileMenuItems = getProfileMenuItems();

  // Search effect
  useEffect(() => {
    const handleSearch = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults({
          courses: [],
          forums: [],
          discussions: [],
          mentors: [],
        });
        return;
      }
       setSearching(true);
       try {
         const data = await coursesApi.globalSearch(searchQuery, undefined, 1, 5);
         setSearchResults({
           courses: data?.courses || [],
           forums: data?.forums || [],
           discussions: data?.discussions || [],
           mentors: data?.mentors || [],
         });
       } catch (err) {
         console.error("Search error:", err);
       } finally {
         setSearching(false);
       }
    };
    const timer = setTimeout(handleSearch, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="relative w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl animate-fade-in-up"
          >
            <div className="flex items-center gap-3 p-5 border-b border-red-100 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Logout
                </h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to logout?
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen bg-white shadow-xl transition-all duration-300 ease-in-out flex flex-col ${
          sidebarCollapsed && !isMobile ? "w-20" : "w-72"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Logo Section */}
        <div
          className={`p-5 border-b border-gray-100 ${sidebarCollapsed && !isMobile ? "px-3" : ""}`}
        >
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              {(!sidebarCollapsed || isMobile) && (
                <div className="font-extrabold text-center">
                  <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white  transition-all  pl-2 py-0.5 ">
                    Learn
                  </span>
                  <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5 ">
                    Hub
                  </span>
                </div>
              )}
            </Link>
            {!isMobile && (
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* User Info */}
        <div
          className={`p-4 border-b border-gray-100 ${sidebarCollapsed && !isMobile ? "px-3" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {getUserAvatar() ? (
                <img
                  src={getUserAvatar()!}
                  alt="Profile"
                  className="object-cover w-10 h-10 rounded-full ring-2 ring-orange-500"
                />
              ) : (
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 ring-2 ring-orange-500">
                  <span className="text-sm font-bold text-white">
                    {getUserInitials()}
                  </span>
                </div>
              )}
            </div>
            {(!sidebarCollapsed || isMobile) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {getUserName()}
                </p>
                <p className="text-xs text-gray-500 capitalize">{userRole}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div
            className={`space-y-1 ${sidebarCollapsed && !isMobile ? "px-2" : "px-3"}`}
          >
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const handleClick = () => {
                setSidebarOpen(false);
                if (searchOpen) setSearchOpen(false);
              };
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={handleClick}
                  className={({ isActive }) => `
                    flex items-center ${sidebarCollapsed && !isMobile ? "justify-center" : "justify-between"} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative
                    ${
                      isActive
                        ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }
                  `}
                >
                  <div
                    className={`flex items-center gap-3 ${sidebarCollapsed && !isMobile ? "justify-center" : ""}`}
                  >
                    <Icon className="w-5 h-5" />
                    {(!sidebarCollapsed || isMobile) && (
                      <span>{item.name}</span>
                    )}
                  </div>
                  {(!sidebarCollapsed || isMobile) &&
                    item.badge &&
                    item.badge > 0 && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full ${
                          location.pathname === item.href
                            ? "bg-white/20 text-white"
                            : item.name === "Messages"
                              ? "bg-blue-100 text-blue-600"
                              : "bg-orange-100 text-orange-600"
                        }`}
                      >
                        {formatBadgeCount(item.badge)}
                      </span>
                    )}
                  {sidebarCollapsed &&
                    !isMobile &&
                    item.badge &&
                    item.badge > 0 && (
                      <span
                        className={`absolute -top-1 -right-1 w-5 h-5 text-[10px] font-bold text-white rounded-full flex items-center justify-center shadow-md ${
                          item.name === "Messages"
                            ? "bg-blue-500"
                            : "bg-red-500"
                        }`}
                      >
                        {formatBadgeCount(item.badge)}
                      </span>
                    )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div
          className={`p-4 border-t border-gray-100 ${sidebarCollapsed && !isMobile ? "px-3" : ""}`}
        >
          <button
            onClick={handleLogoutClick}
            className={`flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-red-600 transition-all duration-200 group ${
              sidebarCollapsed && !isMobile ? "justify-center" : ""
            }`}
          >
            <LogOut className="w-5 h-5 group-hover:text-red-600" />
            {(!sidebarCollapsed || isMobile) && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${!sidebarCollapsed || isMobile ? "lg:pl-72" : "lg:pl-20"}`}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-gray-100 shadow-sm bg-white/95 backdrop-blur-sm">
          <div className="flex items-center justify-between px-4 py-3 md:px-6">
            {/* Left side - Menu button + Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100 lg:hidden"
              >
                <Menu className="w-5 h-5" />
              </button>
              <Link to="/" className="flex items-center gap-2 lg:hidden">
                <div className="font-extrabold text-center">
                  <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white  transition-all  pl-2 py-0.5 ">
                    Learn
                  </span>
                  <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5 ">
                    Hub
                  </span>
                </div>
              </Link>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Messages/Chat - Blue badge */}
              {(user?.role === "student" || user?.role === "mentor") && (
                <Link
                  to={
                    user?.role === "mentor"
                      ? "/dashboard/mentor/messages"
                      : "/dashboard/student/messages"
                  }
                  className="relative p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100"
                >
                  <MessageCircle className="w-5 h-5" />
                  {mentorUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-5 px-1.5 bg-blue-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium shadow-md">
                      {formatBadgeCount(mentorUnreadCount)}
                    </span>
                  )}
                </Link>
              )}

              {/* Notifications - Red badge */}
              <Link
                to="/notifications"
                className="relative p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100"
              >
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium shadow-md">
                    {formatBadgeCount(unreadNotifications)}
                  </span>
                )}
              </Link>

              {/* Profile Dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 p-1 transition-colors rounded-full hover:bg-gray-100"
                >
                  {getUserAvatar() ? (
                    <img
                      src={getUserAvatar()!}
                      alt="Profile"
                      className="object-cover w-8 h-8 border-2 border-orange-500 rounded-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600">
                      <span className="text-xs font-bold text-white">
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                  <ChevronDown
                    className={`hidden sm:block w-4 h-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 z-20 w-56 py-2 mt-2 bg-white border border-gray-100 shadow-lg rounded-xl animate-fade-in-up">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-transparent">
                      <p className="text-sm font-semibold text-gray-900">
                        {getUserName()}
                      </p>
                      <p className="text-xs text-gray-500 capitalize mt-0.5">
                        {userRole}
                      </p>
                    </div>
                    <div className="py-1">
                      {profileMenuItems.map((item) => {
                        const Icon = item.icon;
                        if (item.name === "Logout") {
                          return (
                            <button
                              key={item.name}
                              onClick={item.onClick}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Icon className="w-4 h-4" />
                              {item.name}
                            </button>
                          );
                        }
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsProfileOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Icon className="w-4 h-4 text-gray-500" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Search Modal */}
        {searchOpen && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 animate-fade-in">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setSearchOpen(false)}
            />
            <div className="relative w-full max-w-2xl mx-4 overflow-hidden bg-white shadow-2xl rounded-2xl animate-scale-up">
              <div className="p-5 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-4 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search courses, mentors, forums..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-3 pl-12 pr-12 text-base transition-all border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQuery("");
                    }}
                    className="absolute p-1 text-gray-400 transition-colors -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[60vh]">
                {searching ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-orange-500 rounded-full border-t-transparent animate-spin" />
                    <p className="mt-3 text-sm text-gray-500">Searching...</p>
                  </div>
                ) : searchQuery.length < 2 ? (
                  <div className="py-12 text-center">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-400">
                      Type at least 2 characters to search
                    </p>
                  </div>
                ) : (
                  <div className="py-3">
                    {searchResults.courses.length > 0 && (
                      <div className="px-5 py-3">
                        <p className="mb-3 text-xs font-semibold tracking-wider text-orange-600 uppercase">
                          Courses
                        </p>
                        <div className="space-y-2">
                          {searchResults.courses
                            .slice(0, 3)
                            .map((course: any) => (
                              <Link
                                key={course._id}
                                to={`/course/${course._id}`}
                                onClick={() => setSearchOpen(false)}
                                className="flex items-center gap-3 p-3 transition-all rounded-xl hover:bg-gray-50 group"
                              >
                                <div className="flex-shrink-0 w-12 h-12 overflow-hidden bg-gray-100 rounded-lg">
                                  {course.thumbnail ? (
                                    <img
                                      src={course.thumbnail}
                                      alt=""
                                      className="object-cover w-full h-full transition-transform group-hover:scale-105"
                                    />
                                  ) : (
                                    <BookOpen className="w-full h-full p-2 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 transition-colors group-hover:text-orange-600">
                                    {course.title}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {course.category} • {course.difficulty}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400 transition-colors group-hover:text-orange-500" />
                              </Link>
                            ))}
                        </div>
                      </div>
                    )}
                    {searchResults.mentors.length > 0 && (
                      <div className="px-5 py-3 border-t border-gray-100">
                        <p className="mb-3 text-xs font-semibold tracking-wider text-orange-600 uppercase">
                          Mentors
                        </p>
                        <div className="space-y-2">
                          {searchResults.mentors
                            .slice(0, 3)
                            .map((mentor: any) => (
                              <Link
                                key={mentor._id}
                                to={`/mentor/${mentor._id}`}
                                onClick={() => setSearchOpen(false)}
                                className="flex items-center gap-3 p-3 transition-all rounded-xl hover:bg-gray-50 group"
                              >
                                <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 overflow-hidden rounded-full bg-gradient-to-br from-orange-100 to-orange-200">
                                  {mentor.user?.avatar ? (
                                    <img
                                      src={mentor.user.avatar}
                                      alt=""
                                      className="object-cover w-full h-full"
                                    />
                                  ) : (
                                    <User className="w-5 h-5 text-orange-500" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {mentor.user?.firstName}{" "}
                                    {mentor.user?.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {mentor.specialty}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400 transition-colors group-hover:text-orange-500" />
                              </Link>
                            ))}
                        </div>
                      </div>
                    )}
                    {searchResults.courses.length === 0 &&
                      searchResults.mentors.length === 0 && (
                        <div className="py-12 text-center">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full">
                            <Search className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500">
                            No results found for "{searchQuery}"
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            Try different keywords
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Page Content - Independent Scrolling */}
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scale-up {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.2s ease-out;
        }
        .animate-scale-up {
          animation: scale-up 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DashboardLayout;